import { Button, Dialog, DialogTitle, Stack, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useRef, useEffect } from "react";
import { getHexCode } from "../../util/Color";

// 一括描画する QR 要素数の安全上限。これを超えるとフリーズ/メモリ枯渇するため先頭分のみ描画する。
const MAX_RENDER_QR_COUNT = 300;

// headID..tailID を countStep 刻みで QR ラベル文字列に変換する。
// 安全上限を超える場合は先頭 MAX_RENDER_QR_COUNT 件のみ返し、overflow を true にする。
function buildQrCodeDataList(headID: number, tailID: number, countStep: number) {
  const qrCodeDataList: string[] = [];
  let overflow = false;
  for (let i = headID; i <= tailID; i += countStep) {
    if (qrCodeDataList.length >= MAX_RENDER_QR_COUNT) {
      overflow = true;
      break;
    }
    const qrCount = Math.min(countStep, tailID + 1 - i);
    let suffix = "";
    if (qrCount > 1) {
      suffix = `+${qrCount}`;
    }
    qrCodeDataList.push(`${i}${suffix}`);
  }
  return { qrCodeDataList, overflow };
}

export default function QrListDialog({ opened, headID, tailID, paletteSummarizeCount, onClose }: { opened: boolean; headID: number, tailID: number, paletteSummarizeCount?: number, onClose: () => void }) {
  const countStep = paletteSummarizeCount ?? 1;
  const built = buildQrCodeDataList(headID, tailID, countStep);
  return (
    <Dialog open={opened} onClose={onClose} fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
          <Button
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              zIndex: 1, // 常に一番上
            }}>
            <Typography fontSize={20}>閉じる</Typography>
          </Button>
        </DialogTitle>
      <Stack p={4} gap={2}>
        {built.overflow && (
          <Typography color="error">
            件数が多いため先頭{MAX_RENDER_QR_COUNT}件のみ表示しています。分割して印刷してください。
          </Typography>
        )}
        {built.qrCodeDataList.map(data => {
          return <QRWithEmbeddedText key={data} id={`${data}`} value={`okumura-traceability:1:${data}`} label={data} size={512} />;
        })}
      </Stack>
    </Dialog>
  )
}

// テキスト付きのQRコードを生成します。
function QRWithEmbeddedText({ id, value, label, size = 256 }: { id: string, value: string, label: string, size?: number }) {
  const qrCodeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!qrCodeCanvasRef.current || !canvasRef.current) { 
      return;
    }

    // 余白やサイズ設定
    const padding = 16;
    const labelArea = 40;

    // canvasサイズ設定
    const canvasWidth = Math.ceil(size + padding * 2);
    const canvasHeight = size + labelArea + padding * 2;
    canvasRef.current.width = canvasWidth;
    canvasRef.current.height = canvasHeight;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) { 
      return;
    }

    ctx.fillStyle = getHexCode("White");
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // 中央揃え
    const qrX = (canvasWidth - size) / 2;
    const qrY = padding;
    ctx.drawImage(qrCodeCanvasRef.current, qrX, qrY, size, size);

    // 余白に合わせてテキストを追加
    const fontSize = fitFontSizeToWidth(ctx, label, canvasWidth, labelArea);
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = getHexCode("Black");
    ctx.fillText(label, canvasWidth / 2, qrY + size + (padding / 2));
  }, []);

  
  // 印刷ボタン処理
  const handlePrint = () => {
    if (!canvasRef.current) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    const printWindow = window.open("", "_blank", "width=600,height=600");
    if (printWindow) {
      const head = printWindow.document.head;
      const style = printWindow.document.createElement("style");
      style.textContent = `
        body { margin:0; text-align:center; }
        img { margin-top: 30px; }
      `;
      head.appendChild(style);

      // bodyにqrコードを挿入
      const img = printWindow.document.createElement("img");
      img.src = dataUrl;
      img.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
      printWindow.document.body.appendChild(img);
    }
  };

  return (
    <Button onClick={() => handlePrint()}>
      <Stack>
        <QRCodeCanvas
          id={id}
          value={value}
          size={size}
          ref={qrCodeCanvasRef}
          style={{ display: "none" }}
        />
        <canvas ref={canvasRef} height={size + 40} />
      </Stack>
    </Button>
  );
};


function fitFontSizeToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxFontSize: number) {
  let fontSize = maxFontSize;
  do {
    ctx.font = `${fontSize}px sans-serif`;
    if (ctx.measureText(text).width <= maxWidth) break;
    fontSize -= 1;
  } while (fontSize > 10);
  return fontSize;
}